import type { CompositionV3Filters } from '@revelio/data-access';

import {
  getSkillFilterKeyForLevelId,
  ROLE_LEVEL_ID_TO_FILTER_KEY,
} from './role-skill-ent-kind';

export type DrilldownFilterForV3Merge =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'msa' | 'city';
      id: number;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'role';
      levelId:
        | 'role_k7'
        | 'role_k50'
        | 'role_k150'
        | 'role_k500'
        | 'role_k1500'
        | 'role_k5000'
        | 'role_k15000';
      id: string;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    };

const ROLE_LEVEL_FILTER_KEYS: ReadonlyArray<keyof CompositionV3Filters> = [
  'role_level_1',
  'role_level_2',
  'role_level_3',
  'role_level_4',
  'role_level_5',
  'role_level_6',
  'role_level_7',
  'role_level_8',
];

const SKILL_LEVEL_FILTER_KEYS: ReadonlyArray<keyof CompositionV3Filters> = [
  'skills_level_1',
  'skills_level_2',
  'skills_level_3',
  'skills_level_4',
  'skills_level_5',
  'skills_level_6',
  'skills_level_7',
  'skills_level_8',
];

function clearKeys(
  filters: CompositionV3Filters,
  keys: ReadonlyArray<keyof CompositionV3Filters>
): CompositionV3Filters {
  const next = { ...filters };
  keys.forEach((key) => {
    delete next[key];
  });
  return next;
}

function extractTrailingNumericId(id: string): number | null {
  const last = id.split('_').slice(-1)[0];
  if (last === undefined) return null;
  const n = Number(last);
  return Number.isFinite(n) ? n : null;
}

export function mergeDrilldownIntoCompositionV3Filters(
  base: CompositionV3Filters,
  drilldown: DrilldownFilterForV3Merge
): CompositionV3Filters {
  if (drilldown.segment === 'geography') {
    return {
      ...base,
      region: drilldown.level === 'region' ? [drilldown.id] : base.region,
      country: drilldown.level === 'country' ? [drilldown.id] : base.country,
      msa: drilldown.level === 'msa' ? [drilldown.id] : base.msa,
      city: drilldown.level === 'city' ? [drilldown.id] : base.city,
    };
  }

  if (drilldown.segment === 'rics') {
    return {
      ...base,
      rics_k10: drilldown.level === 'rics_k10' ? [drilldown.id] : base.rics_k10,
      rics_k50: drilldown.level === 'rics_k50' ? [drilldown.id] : base.rics_k50,
      rics_k400:
        drilldown.level === 'rics_k400' || drilldown.level === 'company'
          ? [drilldown.id]
          : base.rics_k400,
    };
  }

  if (drilldown.segment === 'role') {
    const filterKey = ROLE_LEVEL_ID_TO_FILTER_KEY[drilldown.levelId];
    const numericId = Number(drilldown.id);
    if (filterKey === undefined || !Number.isFinite(numericId)) {
      return base;
    }
    // Clear all role_level_* keys so the drilldown selection becomes the
    // single, deepest role filter active. Without this, a pre-existing
    // coarser role selection on the page would override the drilldown.
    const cleared = clearKeys(base, ROLE_LEVEL_FILTER_KEYS);
    return { ...cleared, [filterKey]: [numericId] };
  }

  const skillFilterKey = getSkillFilterKeyForLevelId(drilldown.level);
  const skillNumericId = extractTrailingNumericId(drilldown.id);
  if (skillFilterKey === null || skillNumericId === null) {
    return base;
  }
  const clearedSkill = clearKeys(base, SKILL_LEVEL_FILTER_KEYS);
  return { ...clearedSkill, [skillFilterKey]: [skillNumericId] };
}
